import { useState, useRef, useMemo, useEffect } from "react";
import type { DataReportsPayload } from "../../../api/dataReports";
import { useAdminData } from "../AdminDataContext";
import { useDataReportsPayload } from "../hooks/useDataReportsPayload";
import { buildDataReportsDerived } from "../utils/dataReportsDerived";

type ReportTab = "member" | "activity" | "points" | "operation";
type DateRange = "7d" | "30d" | "90d" | "custom";

function PdfPreviewModal({ payload, onClose }: { payload: DataReportsPayload; onClose: () => void }) {
  const printRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const d = buildDataReportsDerived(payload);
  const cm = payload.coreMetrics;
  const asum = payload.activitySummary;

  const handlePrint = () => {
    setPrinting(true);
    const printContent = printRef.current?.innerHTML || "";
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>${(payload.assocName || "协会").replace(/</g, "")} - 运营数据报表</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: "PingFang SC", "Microsoft YaHei", sans-serif; color: #1f2937; background: white; }
            .pdf-page { padding: 48px; max-width: 794px; margin: 0 auto; }
            h1 { font-size: 22px; font-weight: 700; margin-bottom: 4px; }
            h2 { font-size: 14px; font-weight: 700; margin-bottom: 12px; color: #374151; border-left: 3px solid #ef4444; padding-left: 10px; }
            p { font-size: 12px; color: #6b7280; margin-bottom: 4px; }
            .meta { font-size: 11px; color: #9ca3af; margin-bottom: 32px; }
            .divider { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
            .grid-2 { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; margin-bottom: 20px; }
            .stat-card { background: #f9fafb; border-radius: 8px; padding: 14px; }
            .stat-value { font-size: 24px; font-weight: 700; color: #111827; }
            .stat-label { font-size: 11px; color: #6b7280; margin-top: 2px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 16px; }
            th { background: #f3f4f6; text-align: left; padding: 8px 10px; font-size: 10px; color: #6b7280; font-weight: 600; }
            td { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; }
            .bar-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; font-size: 11px; }
            .bar-label { width: 80px; color: #374151; flex-shrink: 0; }
            .bar-track { flex: 1; height: 6px; background: #f3f4f6; border-radius: 4px; overflow: hidden; }
            .bar-fill { height: 100%; background: #ef4444; border-radius: 4px; }
            .bar-pct { width: 36px; text-align: right; color: #6b7280; flex-shrink: 0; }
            .metric-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px solid #f9fafb; font-size: 12px; }
            .badge-good { color: #16a34a; font-weight: 600; }
            .badge-warn { color: #d97706; font-weight: 600; }
            .section { margin-bottom: 28px; }
            .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; display: flex; justify-content: space-between; }
          </style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      setPrinting(false);
    }, 500);
  };

  const today = d.generatedAtLabel;
  const maxNew = d.maxNewMembers;
  const maxActive = d.maxActive;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {/* Modal Shell */}
      <div className="bg-gray-100 rounded-2xl flex flex-col w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-white border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
              <i className="ri-file-pdf-line text-red-600"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">{payload.assocName || "协会"} - 运营数据报表.pdf</p>
              <p className="text-xs text-gray-400">生成于 {today}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={printing}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
            >
              <i className={`${printing ? "ri-loader-4-line" : "ri-printer-line"}`}></i>
              {printing ? "准备中..." : "打印 / 另存为 PDF"}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer rounded-lg hover:bg-gray-100"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* PDF Page Scroll Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
          {/* The "paper" */}
          <div
            ref={printRef}
            className="pdf-page bg-white mx-auto"
            style={{
              width: "794px",
              minHeight: "1123px",
              padding: "48px",
              fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif",
              color: "#1f2937",
            }}
          >
            {/* Header */}
            <div className="border-b-2 border-red-500 pb-4 mb-6">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <h1 style={{ fontSize: "22px", fontWeight: "700", marginBottom: "4px" }}>运营数据报表</h1>
                  <p style={{ fontSize: "13px", color: "#374151", marginBottom: "2px" }}>{payload.assocName || "协会"}</p>
                  <p className="meta" style={{ fontSize: "11px", color: "#9ca3af" }}>
                    生成时间：{today} &nbsp;｜&nbsp; 报告周期：近 {payload.months} 个月 &nbsp;｜&nbsp; FanHub
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ fontSize: "11px", color: "#9ca3af" }}>内部资料 · 请勿外传</p>
                  <p style={{ fontSize: "11px", color: "#6b7280", marginTop: "4px" }}>页码：1 / 3</p>
                </div>
              </div>
            </div>

            {/* Section 1: Core Metrics */}
            <div className="section" style={{ marginBottom: "28px" }}>
              <h2 style={{ fontSize: "14px", fontWeight: "700", marginBottom: "12px", color: "#374151", borderLeft: "3px solid #ef4444", paddingLeft: "10px" }}>一、核心指标概览</h2>
              <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "10px", marginBottom: "8px" }}>
                {[
                  { label: "会员总数", value: `${cm.totalMembers.toLocaleString()} 人` },
                  { label: "活跃会员", value: `${cm.activeMembers.toLocaleString()} 人` },
                  { label: "本月新增", value: `${cm.newMembersThisMonth} 人` },
                  { label: "已发布活动", value: `${cm.publishedActivitiesCount} 场` },
                ].map((s) => (
                  <div key={s.label} style={{ background: "#f9fafb", borderRadius: "8px", padding: "12px" }}>
                    <div style={{ fontSize: "20px", fontWeight: "700", color: "#111827" }}>{s.value}</div>
                    <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "3px" }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "20px 0" }} />

            {/* Section 2: Member Analysis */}
            <div className="section" style={{ marginBottom: "28px" }}>
              <h2 style={{ fontSize: "14px", fontWeight: "700", marginBottom: "14px", color: "#374151", borderLeft: "3px solid #ef4444", paddingLeft: "10px" }}>二、会员数据分析</h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                {/* Growth chart */}
                <div>
                  <p style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginBottom: "10px" }}>近6个月新增会员</p>
                  {d.memberData.map((row) => (
                    <div key={row.month} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px", fontSize: "11px" }}>
                      <span style={{ width: "30px", color: "#6b7280", flexShrink: 0 }}>{row.month}</span>
                      <div style={{ flex: 1, height: "8px", background: "#f3f4f6", borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ height: "100%", background: "#ef4444", borderRadius: "4px", width: `${(row.new / maxNew) * 100}%` }}></div>
                      </div>
                      <span style={{ width: "30px", textAlign: "right", color: "#374151", fontWeight: "600" }}>+{row.new}</span>
                    </div>
                  ))}
                </div>
                {/* Gender & Age */}
                <div>
                  <p style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginBottom: "10px" }}>性别与年龄分布</p>
                  {d.memberGenderData.map((g) => (
                    <div key={g.label} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px", fontSize: "11px" }}>
                      <span style={{ width: "44px", color: "#6b7280", flexShrink: 0 }}>{g.label}</span>
                      <div style={{ flex: 1, height: "6px", background: "#f3f4f6", borderRadius: "4px", overflow: "hidden" }}>
                        <div style={{ height: "100%", background: "#ef4444", borderRadius: "4px", width: `${g.pct}%` }}></div>
                      </div>
                      <span style={{ width: "36px", textAlign: "right", color: "#374151" }}>{g.pct}%</span>
                    </div>
                  ))}
                  <div style={{ marginTop: "10px" }}>
                    {d.memberAgeData.map((a) => (
                      <div key={a.label} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", fontSize: "10px" }}>
                        <span style={{ width: "60px", color: "#6b7280", flexShrink: 0 }}>{a.label}</span>
                        <div style={{ flex: 1, height: "5px", background: "#f3f4f6", borderRadius: "4px", overflow: "hidden" }}>
                          <div style={{ height: "100%", background: "#fca5a5", borderRadius: "4px", width: `${a.pct}%` }}></div>
                        </div>
                        <span style={{ width: "28px", textAlign: "right", color: "#9ca3af" }}>{a.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Level distribution */}
              <div style={{ marginTop: "14px" }}>
                <p style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginBottom: "10px" }}>会员等级分布</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "8px" }}>
                  {d.memberLevelData.map((lv) => {
                    const pct = Math.round((lv.value / d.memberLevelTotal) * 100);
                    return (
                      <div key={lv.label} style={{ background: "#f9fafb", borderRadius: "8px", padding: "10px", textAlign: "center" }}>
                        <div style={{ fontSize: "18px", fontWeight: "700", color: "#111827" }}>{lv.value}</div>
                        <div style={{ fontSize: "10px", color: "#6b7280", marginTop: "2px" }}>{lv.label}</div>
                        <div style={{ fontSize: "10px", color: "#9ca3af" }}>{pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "20px 0" }} />

            {/* Section 3: Activity */}
            <div className="section" style={{ marginBottom: "28px" }}>
              <h2 style={{ fontSize: "14px", fontWeight: "700", marginBottom: "14px", color: "#374151", borderLeft: "3px solid #ef4444", paddingLeft: "10px" }}>三、活动数据明细</h2>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    {["活动名称", "日期", "报名/容量", "实际出席", "出席率", "收入"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: "10px", color: "#6b7280", fontWeight: "600" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.activityData.map((a, i) => {
                    const rate = a.attended > 0 ? Math.round((a.attended / a.enrolled) * 100) : null;
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 10px", fontWeight: "500", color: "#111827" }}>{a.name}</td>
                        <td style={{ padding: "8px 10px", color: "#6b7280" }}>{a.date}</td>
                        <td style={{ padding: "8px 10px", color: "#374151" }}>{a.enrolled}/{a.capacity}</td>
                        <td style={{ padding: "8px 10px", color: "#374151" }}>{a.attended > 0 ? `${a.attended}人` : "—"}</td>
                        <td style={{ padding: "8px 10px", color: rate ? (rate >= 90 ? "#16a34a" : "#d97706") : "#9ca3af", fontWeight: "600" }}>{rate ? `${rate}%` : "—"}</td>
                        <td style={{ padding: "8px 10px", color: "#111827", fontWeight: "600" }}>{a.revenue}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ display: "flex", gap: "24px", marginTop: "10px", fontSize: "11px", color: "#6b7280" }}>
                <span>累计营收：<strong style={{ color: "#111827" }}>{asum.totalRevenueDisplay}</strong></span>
                <span>平均出席率：<strong style={{ color: "#16a34a" }}>{asum.avgAttendRate}%</strong></span>
                <span>总报名人次：<strong style={{ color: "#111827" }}>{asum.totalRegistrations} 人次</strong></span>
              </div>
            </div>

            <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "20px 0" }} />

            {/* Section 4: Operations */}
            <div className="section" style={{ marginBottom: "28px" }}>
              <h2 style={{ fontSize: "14px", fontWeight: "700", marginBottom: "14px", color: "#374151", borderLeft: "3px solid #ef4444", paddingLeft: "10px" }}>四、核心运营指标</h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                {d.keyMetrics.map((m) => (
                  <div key={m.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f9fafb", borderRadius: "8px", fontSize: "12px" }}>
                    <span style={{ color: "#374151" }}>{m.label}</span>
                    <span style={{ fontWeight: "700", color: m.status === "good" ? "#16a34a" : "#d97706" }}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Active trend */}
            <div style={{ marginBottom: "28px" }}>
              <p style={{ fontSize: "12px", fontWeight: "600", color: "#374151", marginBottom: "10px" }}>活跃会员月度趋势</p>
              {d.memberData.map((row) => (
                <div key={row.month} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "7px", fontSize: "11px" }}>
                  <span style={{ width: "30px", color: "#6b7280", flexShrink: 0 }}>{row.month}</span>
                  <div style={{ flex: 1, height: "8px", background: "#f3f4f6", borderRadius: "4px", overflow: "hidden" }}>
                    <div style={{ height: "100%", background: "#f87171", borderRadius: "4px", width: `${(row.active / maxActive) * 100}%` }}></div>
                  </div>
                  <span style={{ width: "40px", textAlign: "right", color: "#374151", fontWeight: "600" }}>{row.active}</span>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div style={{ marginTop: "40px", paddingTop: "16px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", fontSize: "10px", color: "#9ca3af" }}>
              <span>{payload.assocName || "协会"} · FanHub 协会管理系统</span>
              <span>本报告由系统自动生成，数据仅供内部参考</span>
              <span>{today}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LevelDonutChart({ data }: { data: Array<{ label: string; value: number; hex: string; color: string }> }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const cx = 50, cy = 50, r = 34;
  const circumference = 2 * Math.PI * r;

  let cumulative = 0;
  const segments = data.map((d) => {
    const pct = d.value / total;
    const dash = pct * circumference;
    const offset = circumference - cumulative * circumference / total * total;
    const seg = { ...d, dash, offset: circumference * (1 - cumulative / total), pct: Math.round(pct * 100) };
    cumulative += d.value;
    return seg;
  });

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative" style={{ width: 120, height: 120 }}>
        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f3f4f6" strokeWidth="12" />
          {segments.map((seg, i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={seg.hex}
              strokeWidth="12"
              strokeDasharray={`${seg.dash} ${circumference}`}
              strokeDashoffset={seg.offset}
              strokeLinecap="butt"
            />
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-base font-bold text-gray-900">{total.toLocaleString()}</p>
          <p className="text-[10px] text-gray-400">总会员</p>
        </div>
      </div>
      <div className="w-full space-y-1.5">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.hex }}></div>
            <span className="text-[11px] text-gray-600 flex-1 truncate">{seg.label}</span>
            <span className="text-[11px] font-semibold text-gray-700 flex-shrink-0">{seg.value}</span>
            <span className="text-[10px] text-gray-400 w-8 text-right flex-shrink-0">{seg.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DataReports() {
  const { tenantLegacyId } = useAdminData();
  const [activeTab, setActiveTab] = useState<ReportTab>("member");
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pointsLevelFilter, setPointsLevelFilter] = useState<string>("全部");

  const { payload, loading, error, reload } = useDataReportsPayload(tenantLegacyId, dateRange);
  const d = useMemo(() => buildDataReportsDerived(payload), [payload]);

  const maxNewMembers = d.maxNewMembers;
  const maxActive = d.maxActive;

  useEffect(() => {
    if (payload && !d.levelOptions.includes(pointsLevelFilter)) {
      setPointsLevelFilter("全部");
    }
  }, [payload, d.levelOptions, pointsLevelFilter]);

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => {
      setExporting(false);
      setExportDone(true);
      setTimeout(() => setExportDone(false), 2000);
    }, 1200);
  };

  const cm = d.coreMetrics;
  const growthNote =
    cm && cm.newMembersPrevMonth > 0
      ? `${Math.round(((cm.newMembersThisMonth - cm.newMembersPrevMonth) / cm.newMembersPrevMonth) * 100)}% 较上月`
      : "—";

  return (
    <div>
      {loading && (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
          <i className="ri-loader-4-line animate-spin" />
          正在加载报表数据…
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-center justify-between gap-3">
          <span>加载失败：{error}</span>
          <button type="button" onClick={() => void reload()} className="text-red-600 font-medium underline cursor-pointer whitespace-nowrap">
            重试
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">数据报表</h2>
          <p className="text-xs text-gray-400 mt-0.5">协会运营数据统计与导出</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            {(["7d", "30d", "90d"] as const).map((r) => (
              <button
                key={r}
                onClick={() => setDateRange(r)}
                className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${
                  dateRange === r ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {r === "7d" ? "近7天" : r === "30d" ? "近30天" : "近90天"}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowPdfPreview(true)}
            disabled={!payload}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-file-pdf-line"></i>
            PDF 预览
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap ${
              exportDone ? "bg-green-500 text-white" : exporting ? "bg-gray-200 text-gray-500 cursor-wait" : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            <i className={exportDone ? "ri-check-line" : exporting ? "ri-loader-4-line" : "ri-download-line"}></i>
            {exportDone ? "导出成功" : exporting ? "导出中..." : "导出 Excel"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
        {([
          { id: "member",   label: "会员分析",   icon: "ri-group-line" },
          { id: "activity", label: "活动数据",   icon: "ri-calendar-event-line" },
          { id: "points",   label: "积分概况",   icon: "ri-copper-coin-line" },
          { id: "operation",label: "运营概况",   icon: "ri-line-chart-line" },
        ] as { id: ReportTab; label: string; icon: string }[]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm cursor-pointer transition-all whitespace-nowrap ${
              activeTab === tab.id ? "bg-white text-gray-800 font-medium shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Member Analysis */}
      {activeTab === "member" && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            {[
              {
                label: "会员总数",
                value: cm ? cm.totalMembers.toLocaleString() : "—",
                change: cm ? `+${cm.newMembersThisMonth} 本月` : loading ? "…" : "暂无",
              },
              {
                label: "活跃会员",
                value: cm ? cm.activeMembers.toLocaleString() : "—",
                change: cm && cm.totalMembers > 0 ? `占 ${((cm.activeMembers / cm.totalMembers) * 100).toFixed(1)}%` : "—",
              },
              {
                label: "本月新增",
                value: cm ? String(cm.newMembersThisMonth) : "—",
                change: growthNote,
              },
              {
                label: "流失预警",
                value: cm ? String(cm.churnWarning) : "—",
                change: "近90天无互动",
              },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl p-4">
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                <p className="text-xs text-gray-400 mt-1">{s.change}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">近6个月会员增长趋势</h3>
            <div className="flex items-end gap-3 h-36">
              {d.memberData.map((row) => (
                <div key={row.month} className="flex-1 flex flex-col items-center gap-1.5">
                  <span className="text-xs text-gray-500">{row.new}</span>
                  <div className="w-full bg-red-500 rounded-t-lg transition-all min-h-[4px]" style={{ height: `${(row.new / maxNewMembers) * 100}px` }}></div>
                  <span className="text-xs text-gray-400">{row.month}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">性别分布</h3>
              <div className="space-y-3">
                {d.memberGenderData.map((g) => (
                  <div key={g.label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600">{g.label}</span>
                      <span className="text-gray-500 font-medium">{g.value} 人（{g.pct}%）</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: `${g.pct}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">年龄分布</h3>
              <div className="space-y-2.5">
                {d.memberAgeData.map((a) => (
                  <div key={a.label}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600">{a.label}</span>
                      <span className="text-gray-500 font-medium">{a.pct}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-red-500 to-red-300 rounded-full" style={{ width: `${a.pct}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Level distribution with donut chart */}
            <div className="bg-white rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">会员等级分布</h3>
              <LevelDonutChart data={d.memberLevelData} />
            </div>
          </div>

          {/* Level bar chart */}
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700">各等级人数对比</h3>
              <span className="text-xs text-gray-400">共 {d.memberLevelTotal.toLocaleString()} 名会员</span>
            </div>
            <div className="space-y-3">
              {d.memberLevelData.map((lv) => {
                const pct = Math.round((lv.value / d.memberLevelTotal) * 100);
                const maxLv = Math.max(1, ...d.memberLevelData.map((x) => x.value));
                const barWidth = (lv.value / maxLv) * 100;
                return (
                  <div key={lv.label} className="flex items-center gap-4">
                    <div className="flex items-center gap-2 w-24 flex-shrink-0">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: lv.hex }}></div>
                      <span className="text-xs text-gray-600 truncate">{lv.label}</span>
                    </div>
                    <div className="flex-1 h-6 bg-gray-50 rounded-xl overflow-hidden relative">
                      <div
                        className="h-full rounded-xl transition-all duration-700 flex items-center justify-end pr-3"
                        style={{ width: `${barWidth}%`, backgroundColor: lv.hex, opacity: 0.85 }}
                      >
                        <span className="text-white text-[10px] font-bold drop-shadow-sm whitespace-nowrap">{lv.value} 人</span>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-gray-500 w-8 text-right flex-shrink-0">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Points Overview */}
      {activeTab === "points" && (() => {
        const activeMonthlyData = d.pointsByLevelData[pointsLevelFilter] ?? d.pointsMonthlyData;
        const totalIssued   = activeMonthlyData.reduce((s, row) => s + row.issued, 0);
        const totalConsumed = activeMonthlyData.reduce((s, row) => s + row.consumed, 0);
        const maxIssued     = Math.max(1, ...activeMonthlyData.map((row) => row.issued));
        const maxCount      = Math.max(1, ...d.topRedeemProducts.map((p) => p.count));
        const grandIssued = d.pointsMonthlyData.reduce((s, row) => s + row.issued, 0);
        const rs = d.redeemSummary;
        return (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: "总发放积分",   value: totalIssued.toLocaleString(),   sub: "近6个月累计", icon: "ri-add-circle-line",    bg: "bg-red-50 text-red-600" },
                { label: "总消耗积分",   value: totalConsumed.toLocaleString(), sub: "近6个月累计", icon: "ri-indeterminate-circle-line", bg: "bg-amber-50 text-amber-600" },
                { label: "积分净余",     value: (totalIssued - totalConsumed).toLocaleString(), sub: "当前积分池", icon: "ri-safe-line", bg: "bg-teal-50 text-teal-600" },
                { label: "参与兑换人数", value: rs ? String(rs.distinctRedeemers) : "—", sub: "统计周期内", icon: "ri-exchange-line",      bg: "bg-gray-50 text-gray-600" },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-2xl p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0 ${s.bg}`}>
                    <i className={`${s.icon} text-gray-500`}></i>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                    <p className="text-xs text-gray-400">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Monthly issued vs consumed */}
            <div className="bg-white rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">近6个月积分发放 vs 消耗趋势</h3>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-red-500 inline-block"></span>发放</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm bg-amber-400 inline-block"></span>消耗</span>
                </div>
              </div>

              {/* Level filter */}
              <div className="flex items-center gap-1.5 mb-4 flex-wrap">
                <span className="text-xs text-gray-400 mr-1">按等级查看：</span>
                {d.levelOptions.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setPointsLevelFilter(opt)}
                    className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                      pointsLevelFilter === opt
                        ? "bg-red-600 text-white"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>

              {/* Show proportion hint when not showing all */}
              {pointsLevelFilter !== "全部" && (
                <div className="mb-4 flex items-center gap-2 bg-red-50 rounded-xl px-3 py-2">
                  <i className="ri-information-line text-red-500 text-sm flex-shrink-0"></i>
                  <p className="text-xs text-red-600">
                    当前显示「{pointsLevelFilter}」的积分数据，
                    占总发放积分的{" "}
                    <strong>
                      {grandIssued > 0 ? Math.round((totalIssued / grandIssued) * 100) : 0}%
                    </strong>
                  </p>
                </div>
              )}

              <div className="space-y-4">
                {activeMonthlyData.map((row) => (
                  <div key={row.month} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-8 flex-shrink-0 text-right">{row.month}</span>
                    <div className="flex-1 space-y-1.5">
                      {/* Issued */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-5 bg-gray-50 rounded-lg overflow-hidden">
                          <div
                            className="h-full bg-red-500 rounded-lg flex items-center justify-end pr-2 transition-all duration-500"
                            style={{ width: `${(row.issued / maxIssued) * 100}%` }}
                          >
                            <span className="text-white text-xs font-bold drop-shadow-sm whitespace-nowrap">{row.issued.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      {/* Consumed */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-5 bg-gray-50 rounded-lg overflow-hidden">
                          <div
                            className="h-full bg-amber-400 rounded-lg flex items-center justify-end pr-2 transition-all duration-500"
                            style={{ width: `${(row.consumed / maxIssued) * 100}%` }}
                          >
                            <span className="text-white text-xs font-bold drop-shadow-sm whitespace-nowrap">{row.consumed.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 w-12 text-right flex-shrink-0">
                      <span className={`font-semibold ${row.issued - row.consumed > 0 ? "text-teal-600" : "text-red-500"}`}>
                        {row.issued - row.consumed > 0 ? "+" : ""}{(row.issued - row.consumed).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Level proportion comparison (only show in 全部 mode) */}
              {pointsLevelFilter === "全部" && (
                <div className="mt-5 pt-4 border-t border-gray-50">
                  <p className="text-xs font-semibold text-gray-600 mb-3">各等级积分发放占比（近6个月累计）</p>
                  <div className="space-y-2">
                    {d.levelOptions.filter((o) => o !== "全部").map((level, i) => {
                      const levelTotal = (d.pointsByLevelData[level] ?? []).reduce((s, row) => s + row.issued, 0);
                      const grandTotal = grandIssued;
                      const pct = grandTotal > 0 ? Math.round((levelTotal / grandTotal) * 100) : 0;
                      const colors = ["#ef4444", "#f59e0b", "#f97316", "#9ca3af"];
                      return (
                        <div key={level} className="flex items-center gap-3">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[i] }}></div>
                          <span className="text-xs text-gray-600 w-20 flex-shrink-0">{level}</span>
                          <div className="flex-1 h-4 bg-gray-50 rounded-lg overflow-hidden">
                            <div
                              className="h-full rounded-lg flex items-center justify-end pr-2 transition-all duration-500"
                              style={{ width: `${pct}%`, backgroundColor: colors[i], opacity: 0.8 }}
                            >
                              <span className="text-white text-[10px] font-bold drop-shadow-sm whitespace-nowrap">{levelTotal.toLocaleString()}</span>
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-gray-500 w-8 text-right flex-shrink-0">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Top redemption products */}
            <div className="bg-white rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700">兑换商品排行榜</h3>
                <span className="text-xs text-gray-400">近30天兑换次数</span>
              </div>
              <div className="space-y-4">
                {d.topRedeemProducts.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-4">
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 text-xs font-bold ${
                      i === 0 ? "bg-amber-400 text-white" : i === 1 ? "bg-gray-300 text-white" : i === 2 ? "bg-amber-700/60 text-white" : "bg-gray-100 text-gray-500"
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-800 truncate">{p.name}</span>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                          <span className="text-xs text-gray-400">{p.points} 积分</span>
                          <span className="text-sm font-bold text-gray-800">{p.count} 次</span>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${(p.count / maxCount) * 100}%`, backgroundColor: p.color }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between text-xs text-gray-500">
                <span>累计兑换次数 <strong className="text-gray-800">{rs ? `${rs.totalOrders} 次` : "—"}</strong></span>
                <span>消耗积分 <strong className="text-red-600">{rs ? rs.totalPointsConsumed.toLocaleString() : "—"}</strong></span>
                <span>最受欢迎 <strong className="text-gray-800">{rs?.topName ?? "—"}</strong></span>
              </div>
            </div>

            {/* Points distribution by source */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">积分来源分布</h3>
                <div className="space-y-3">
                  {d.pointsSourceDistribution.map((src) => (
                    <div key={src.label}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-gray-600">{src.label}</span>
                        <span className="text-gray-500 font-medium">{src.value.toLocaleString()} 分（{src.pct}%）</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-red-500 to-red-300 rounded-full" style={{ width: `${src.pct}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">各等级会员积分概况</h3>
                <div className="space-y-3">
                  {d.levelPointsOverview.map((lp) => (
                    <div key={lp.label} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: lp.color }}></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700">{lp.label}</p>
                        <p className="text-xs text-gray-400">人均 {lp.avg} 积分</p>
                      </div>
                      <span className="text-sm font-bold text-gray-800 flex-shrink-0">{lp.total}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Activity Data */}
      {activeTab === "activity" && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            {[
              {
                label: "本月活动数",
                value: d.activitySummary ? String(d.activitySummary.monthlyActivities) : "—",
                sub: "场",
              },
              {
                label: "总报名人次",
                value: d.activitySummary ? String(d.activitySummary.totalRegistrations) : "—",
                sub: "人次",
              },
              {
                label: "平均出席率",
                value: d.activitySummary ? `${d.activitySummary.avgAttendRate}%` : "—",
                sub: "",
              },
              {
                label: "活动总收入",
                value: d.activitySummary?.totalRevenueDisplay ?? "—",
                sub: "",
              },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl p-4">
                <p className="text-2xl font-bold text-gray-900">{s.value}<span className="text-sm text-gray-400 ml-1">{s.sub}</span></p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* ── 活动维度分析图表 ── */}
          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">报名率 vs 到场率对比分析</h3>
                <p className="text-xs text-gray-400 mt-0.5">各活动满员比例与实际出席情况横向对比</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-red-500 inline-block"></span> 报名率
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-teal-500 inline-block"></span> 到场率
                </span>
              </div>
            </div>
            <div className="px-5 py-5 space-y-4">
              {d.activityAnalysis.map((a) => (
                <div key={a.shortName} className="group">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-800 truncate max-w-[180px]">{a.fullName}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 flex-shrink-0">{a.type}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs flex-shrink-0">
                      <span className="text-gray-500">{a.enrolled}/{a.capacity} 人报名</span>
                      {a.attended !== null && a.attended > 0 ? (
                        <span className="text-gray-500">{a.attended} 人到场</span>
                      ) : (
                        <span className="text-gray-300">未开始</span>
                      )}
                    </div>
                  </div>
                  {/* Bar: enrollment rate */}
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs text-gray-400 w-10 flex-shrink-0 text-right">报名率</span>
                    <div className="flex-1 h-5 bg-gray-50 rounded-lg overflow-hidden relative">
                      <div
                        className="h-full bg-red-500 rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ width: `${a.enrollRate}%` }}
                      >
                        <span className="text-white text-xs font-bold drop-shadow-sm">{a.enrollRate}%</span>
                      </div>
                    </div>
                  </div>
                  {/* Bar: attendance rate */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-10 flex-shrink-0 text-right">到场率</span>
                    <div className="flex-1 h-5 bg-gray-50 rounded-lg overflow-hidden relative">
                      {a.attendRate !== null ? (
                        <div
                          className="h-full bg-teal-500 rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                          style={{ width: `${a.attendRate}%` }}
                        >
                          <span className="text-white text-xs font-bold drop-shadow-sm">{a.attendRate}%</span>
                        </div>
                      ) : (
                        <div className="h-full flex items-center px-3">
                          <span className="text-xs text-gray-300">活动未开始，暂无数据</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 活动类型维度统计 ── */}
          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">活动类型维度分析</h3>
              <p className="text-xs text-gray-400 mt-0.5">各类型活动的平均报名率和到场率汇总</p>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-5 gap-3 mb-5">
                {d.activityTypeStats.map((s) => (
                  <div key={s.type} className="rounded-xl border border-gray-100 p-3 text-center">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mb-2 ${s.light}`}>{s.type}</span>
                    <p className="text-lg font-bold text-gray-900">{s.count} <span className="text-xs font-normal text-gray-400">场</span></p>
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                {d.activityTypeStats.map((s) => (
                  <div key={s.type} className="flex items-center gap-4">
                    <span className={`text-xs px-2 py-1 rounded-lg font-medium w-20 text-center flex-shrink-0 ${s.light}`}>{s.type}</span>
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      {/* Enroll rate */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-10 flex-shrink-0">报名率</span>
                        <div className="flex-1 h-4 bg-gray-50 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${s.color} rounded-full transition-all`}
                            style={{ width: `${s.avgEnrollRate}%`, opacity: 0.85 }}
                          ></div>
                        </div>
                        <span className="text-xs font-semibold text-gray-700 w-8 flex-shrink-0">{s.avgEnrollRate}%</span>
                      </div>
                      {/* Attend rate */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 w-10 flex-shrink-0">到场率</span>
                        <div className="flex-1 h-4 bg-gray-50 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-teal-500 rounded-full transition-all"
                            style={{ width: `${s.avgAttendRate}%`, opacity: 0.8 }}
                          ></div>
                        </div>
                        <span className="text-xs font-semibold text-teal-700 w-8 flex-shrink-0">{s.avgAttendRate}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Top 3 Highlights ── */}
          <div className="grid grid-cols-3 gap-3">
            {d.activityHighlights.map((h) => (
              <div key={h.label} className="bg-white rounded-2xl p-4 flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${h.color}`}>
                  <i className={`${h.icon} text-lg`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-400">{h.label}</p>
                  <p className="text-sm font-semibold text-gray-800 truncate">{h.value}</p>
                  <p className="text-xs text-gray-500">{h.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* existing detail table */}
          <div className="bg-white rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">活动明细数据</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {["活动名称", "日期", "报名/容量", "实际出席", "出席率", "收入"].map((h) => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {d.activityData.map((a, i) => {
                  const attendRate = a.attended > 0 ? Math.round((a.attended / a.enrolled) * 100) : null;
                  return (
                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3 text-gray-800 font-medium">{a.name}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{a.date}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-red-500 rounded-full" style={{ width: `${(a.enrolled / a.capacity) * 100}%` }}></div>
                          </div>
                          <span className="text-xs text-gray-500">{a.enrolled}/{a.capacity}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">{a.attended > 0 ? `${a.attended} 人` : <span className="text-gray-300">未开始</span>}</td>
                      <td className="px-4 py-3">
                        {attendRate !== null ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${attendRate >= 90 ? "bg-green-50 text-green-600" : attendRate >= 70 ? "bg-amber-50 text-amber-600" : "bg-red-50 text-red-500"}`}>{attendRate}%</span>
                        ) : (
                          <span className="text-gray-300 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-sm font-medium">{a.revenue}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Operation Overview */}
      {activeTab === "operation" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">活跃会员月度趋势</h3>
              <div className="flex items-end gap-2 h-28">
                {d.memberData.map((row) => (
                  <div key={row.month} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-400">{row.active}</span>
                    <div className="w-full bg-gradient-to-t from-red-500 to-red-400 rounded-t-lg transition-all min-h-[4px]" style={{ height: `${(row.active / maxActive) * 88}px` }}></div>
                    <span className="text-xs text-gray-400">{row.month}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">核心运营指标</h3>
              <div className="space-y-3">
                {d.keyMetrics.map((m) => (
                  <div key={m.label} className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">{m.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-800">{m.value}</span>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${m.status === "good" ? "bg-green-500" : "bg-amber-400"}`}></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Export Options */}
          <div className="bg-white rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">数据导出</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "会员名单", desc: "包含姓名、联系方式、等级、注册时间", icon: "ri-group-line", fields: "Excel / CSV" },
                { label: "活动报名记录", desc: "报名人员、时间、状态、出席记录", icon: "ri-calendar-event-line", fields: "Excel" },
                {
                  label: "运营数据报表", desc: "会员增长、活跃度、投票参与等综合数据", icon: "ri-file-pdf-line", fields: "PDF / Excel",
                  isPdf: true
                },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() =>
                    (item as { isPdf?: boolean }).isPdf
                      ? payload && setShowPdfPreview(true)
                      : handleExport()
                  }
                  className="text-left p-4 rounded-xl border border-gray-100 hover:border-red-200 hover:bg-red-50/30 cursor-pointer transition-all group"
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-xl mb-3 group-hover:bg-red-100 transition-colors">
                    <i className={`${item.icon} text-gray-500 group-hover:text-red-600 transition-colors`}></i>
                  </div>
                  <p className="text-sm font-semibold text-gray-800 mb-1">{item.label}</p>
                  <p className="text-xs text-gray-400 mb-2 leading-relaxed">{item.desc}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <i className="ri-download-line"></i> {item.fields}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPdfPreview && payload && <PdfPreviewModal payload={payload} onClose={() => setShowPdfPreview(false)} />}
    </div>
  );
}
